const WHALE_EXECUTION_STEP_KINDS = new Set([
    "delegate_start",
    "delegate",
    "tool",
    "tool_start",
    "script",
    "script_start",
    "model",
    "model_start",
    "web_ingest",
    "web_ingest_start",
]);
/** Step SSE che indica che l'agente ha abbastanza contesto per eseguire il task. */
export function isWhaleExecutionStep(kind) {
    return WHALE_EXECUTION_STEP_KINDS.has(kind);
}
const TERMINAL_JOB_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELLED"]);
export function isTerminalAIJobStatus(status) {
    return TERMINAL_JOB_STATUSES.has(String(status || "").toUpperCase());
}
export function parseOutputTextFromProviderUsage(providerUsageJson) {
    if (!providerUsageJson)
        return null;
    try {
        const u = JSON.parse(providerUsageJson);
        return u.outputText?.trim() || null;
    }
    catch {
        return null;
    }
}
/** Output text from the mission `complete` step (stream + async jobs). */
export function parseOutputTextFromMissionSteps(steps) {
    const completeSteps = steps.filter((s) => s.kind === "complete");
    if (!completeSteps.length)
        return null;
    const latest = completeSteps.reduce((a, b) => (a.seq >= b.seq ? a : b));
    const detail = parseWhaleStepDetail(latest);
    const output = String(detail.output ?? "").trim();
    return output || null;
}
/** Client-side cascade for live Whale stream (before async S3/job fetch). */
export function resolveWhaleRawDeliverable(outputText, finalOutput, liveOutput, executionStarted, steps) {
    const fromOutput = outputText?.trim();
    if (fromOutput)
        return fromOutput;
    const fromFinal = finalOutput.trim();
    if (fromFinal)
        return fromFinal;
    if (steps?.length) {
        const fromSteps = parseOutputTextFromMissionSteps(steps);
        if (fromSteps)
            return fromSteps;
    }
    const fromLive = liveOutput.trim();
    if (fromLive)
        return fromLive;
    if (executionStarted)
        return "";
    return "";
}
function parseWhaleStepDetail(step) {
    if (!step.detailJson)
        return {};
    try {
        return JSON.parse(step.detailJson);
    }
    catch {
        return {};
    }
}
/** Compact live trace for agent_task widget; groups parallel delegate steps from the same round. */
export function formatWhaleStepLines(steps) {
    if (!steps.length)
        return "Task Whale in corso…";
    const lines = [];
    const consumed = new Set();
    for (const step of steps) {
        if (consumed.has(step.seq))
            continue;
        const detail = parseWhaleStepDetail(step);
        const roundId = detail.delegateRoundId;
        const parallel = detail.parallel === true;
        if (parallel &&
            roundId != null &&
            (step.kind === "delegate" || step.kind === "delegate_start")) {
            const group = steps.filter((s) => {
                if (s.kind !== "delegate" && s.kind !== "delegate_start")
                    return false;
                const d = parseWhaleStepDetail(s);
                return d.parallel === true && d.delegateRoundId === roundId;
            });
            for (const g of group)
                consumed.add(g.seq);
            const labels = group
                .map((s) => {
                const d = parseWhaleStepDetail(s);
                const peer = String(d.peerName || "").trim();
                if (peer)
                    return peer;
                return s.summary?.trim() || s.kind;
            })
                .filter(Boolean);
            lines.push(`• In parallelo: ${labels.join(", ")}`);
            continue;
        }
        if (step.kind === "plan") {
            lines.push(`• ${step.summary?.trim() || "Piano missione"}`);
            continue;
        }
        if (step.kind === "model_start") {
            // Mostra lo stato transitorio solo finché il corrispondente step "model"
            // (emesso a generazione conclusa) non è ancora arrivato.
            const completed = steps.some((s) => s.kind === "model" && s.seq > step.seq);
            if (!completed)
                lines.push("• 🎨 Generazione immagine in corso…");
            continue;
        }
        if (step.kind === "model") {
            lines.push("• 🎨 Immagine generata");
            continue;
        }
        lines.push(`• ${step.summary?.trim() || step.kind}`);
    }
    return lines.join("\n");
}
function peerFromDelegateStep(step, fallback) {
    if (step.kind !== "delegate" && step.kind !== "delegate_start")
        return null;
    let detail = {};
    if (step.detailJson) {
        try {
            detail = JSON.parse(step.detailJson);
        }
        catch {
            detail = {};
        }
    }
    const peerId = String(detail.peerAgentId || detail.peerId || detail.agentId || "").trim();
    const peerName = String(detail.peerName || detail.agentName || "").trim();
    const peerIcon = String(detail.peerIcon || detail.agentIcon || "").trim();
    const peerIconS3Key = String(detail.peerIconS3Key || detail.agentIconS3Key || "").trim();
    if (!peerId && !peerName)
        return null;
    return {
        agentId: peerId || fallback.agentId,
        agentName: peerName || fallback.agentName,
        agentIcon: peerIcon || fallback.agentIcon,
        agentIconS3Key: peerIconS3Key || fallback.agentIconS3Key || null,
    };
}
/** All distinct delegate peers in first-intervention order; excludes orchestrator; fallback if none. */
export function resolveHandlersFromMissionSteps(steps, fallback) {
    const orchestratorKey = fallback.agentId.trim().toLowerCase();
    const seen = new Set();
    const handlers = [];
    const ordered = [...steps].sort((a, b) => a.seq - b.seq);
    for (const step of ordered) {
        const peer = peerFromDelegateStep(step, fallback);
        if (!peer)
            continue;
        const idKey = peer.agentId.trim().toLowerCase();
        if (idKey && idKey === orchestratorKey)
            continue;
        const dedupKey = idKey || peer.agentName.trim().toLowerCase();
        if (!dedupKey || seen.has(dedupKey))
            continue;
        seen.add(dedupKey);
        handlers.push(peer);
    }
    if (handlers.length === 0)
        return [fallback];
    return handlers;
}
export function resolveHandlerFromMissionSteps(steps, fallback) {
    const handlers = resolveHandlersFromMissionSteps(steps, fallback);
    return handlers[handlers.length - 1] ?? fallback;
}
export function serializeAgentHandlersJson(handlers) {
    return JSON.stringify(handlers);
}
export function parseAgentHandlersJson(raw) {
    if (!raw?.trim())
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return null;
        const handlers = [];
        for (const item of parsed) {
            if (!item || typeof item !== "object")
                continue;
            const row = item;
            const agentId = String(row.agentId ?? "").trim();
            const agentName = String(row.agentName ?? "").trim();
            if (!agentId && !agentName)
                continue;
            handlers.push({
                agentId,
                agentName: agentName || agentId,
                agentIcon: String(row.agentIcon ?? "").trim(),
                agentIconS3Key: String(row.agentIconS3Key ?? "").trim() || null,
            });
        }
        return handlers.length > 0 ? handlers : null;
    }
    catch {
        return null;
    }
}
export function agentHandlersForBlock(block, fallback) {
    const persisted = parseAgentHandlersJson(block.payload.agentHandlersJson);
    if (persisted?.length)
        return persisted;
    const authorSub = block.payload.authorSub?.trim() ?? "";
    const authorName = block.payload.authorName?.trim();
    if (authorSub.startsWith("agent:") || authorName) {
        return [
            {
                agentId: authorSub.startsWith("agent:")
                    ? authorSub.slice("agent:".length)
                    : fallback.agentId,
                agentName: authorName || fallback.agentName,
                agentIcon: block.payload.agentIcon?.trim() || fallback.agentIcon,
                agentIconS3Key: block.payload.agentIconS3Key?.trim() || fallback.agentIconS3Key || null,
            },
        ];
    }
    return [fallback];
}
export function childBlocksForParent(blocks, parentId) {
    return blocks.filter((b) => b.payload.parentId === parentId);
}
export function hasAgentResponseChild(blocks, parentId) {
    return childBlocksForParent(blocks, parentId).some((b) => b.payload.relation === "agent_response");
}
export function runningAgentTaskBlocks(blocks) {
    return blocks.filter((b) => b.payload.relation === "agent_task" &&
        b.payload.agentStatus === "running" &&
        Boolean(b.payload.agentJobId?.trim()));
}
const TERMINAL_AGENT_TASK_STATUSES = new Set(["done", "failed", "error"]);
const WIDGET_GOAL_USER_MARKER = "[Current user request]";
const THREAD_GOAL_USER_MARKER = "[Richiesta utente]";
export function isTerminalAgentTaskStatus(status) {
    return TERMINAL_AGENT_TASK_STATUSES.has(String(status || "").trim());
}
/** Extract raw user question from enriched Whale mission goal (widget or thread). */
export function extractUserQuestionFromMissionGoal(goal) {
    const trimmed = String(goal ?? "").trim();
    if (!trimmed)
        return null;
    for (const marker of [WIDGET_GOAL_USER_MARKER, THREAD_GOAL_USER_MARKER]) {
        const idx = trimmed.indexOf(marker);
        if (idx < 0)
            continue;
        let rest = trimmed.slice(idx + marker.length).replace(/^\s+/, "");
        const lines = rest.split("\n");
        const parts = [];
        for (const line of lines) {
            const t = line.trim();
            if (/^\[[^\]]+\]/.test(t))
                break;
            parts.push(line);
        }
        const question = parts.join("\n").trim();
        if (question)
            return question.slice(0, 500);
    }
    return null;
}
export function extractQuestionFromMissionSteps(steps) {
    const startStep = steps.find((s) => s.kind === "start");
    if (!startStep)
        return null;
    const detail = parseWhaleStepDetail(startStep);
    const goal = String(detail.goal ?? "").trim();
    if (!goal)
        return null;
    return extractUserQuestionFromMissionGoal(goal);
}
export function shouldBackfillWhaleAgentQuestion(block, currentUserSub) {
    if (block.payload.relation !== "agent_task")
        return false;
    if (!isTerminalAgentTaskStatus(block.payload.agentStatus))
        return false;
    if (block.payload.agentQuestion?.trim())
        return false;
    if (!block.payload.agentJobId?.trim())
        return false;
    const launchedBy = block.payload.launchedBy?.trim();
    if (!launchedBy || launchedBy !== currentUserSub)
        return false;
    return true;
}
export function shouldReconcileAgentTask(block, currentUserSub) {
    if (block.payload.relation !== "agent_task")
        return false;
    if (block.payload.agentStatus !== "running")
        return false;
    const jobId = block.payload.agentJobId?.trim();
    if (!jobId)
        return false;
    const launchedBy = block.payload.launchedBy?.trim();
    if (!launchedBy || launchedBy !== currentUserSub)
        return false;
    return true;
}
export function resolveHandlerFromStreamStep(step, fallback) {
    return peerFromDelegateStep(step, fallback);
}
export function findLatestAgentTaskChild(blocks, parentId) {
    const tasks = blocks.filter((b) => b.payload.parentId === parentId &&
        b.payload.relation === "agent_task");
    if (!tasks.length)
        return null;
    return tasks.reduce((latest, b) => (b.sortIndex ?? 0) >= (latest.sortIndex ?? 0) ? b : latest);
}
export function throttleWhaleStepFlush(lastFlushAt, intervalMs, force) {
    if (force)
        return true;
    return Date.now() - lastFlushAt >= intervalMs;
}
function tryParseLeadingJsonObject(text) {
    if (!text.startsWith("{"))
        return null;
    let depth = 0;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === "{")
            depth += 1;
        else if (ch === "}") {
            depth -= 1;
            if (depth === 0) {
                const slice = text.slice(0, i + 1);
                try {
                    const obj = JSON.parse(slice);
                    return { obj, rest: text.slice(i + 1).trim() };
                }
                catch {
                    return null;
                }
            }
        }
    }
    return null;
}
function cleanupAgentMarkdown(text) {
    return text
        .replace(/^['`]{2,}\s*/m, "")
        .replace(/['`]{2,}\s*$/m, "")
        .trim();
}
const OPENING_META_RE = /^(Ecco|Di seguito|Come richiesto|Userò|Procedo|Sto per|Ho preparato)\b.*[:.]\s*$/i;
const CLOSING_META_RE = /^(Fammi sapere|Spero (questo )?ti sia utile|Se vuoi posso)\b.*$/i;
function isDeliverableContentLine(line) {
    const t = line.trim();
    if (!t)
        return false;
    if (/^#{1,6}\s/.test(t))
        return true;
    if (/^[-*+]\s/.test(t))
        return true;
    if (/^\d+\.\s/.test(t))
        return true;
    if (/^\|/.test(t))
        return true;
    if (/^```/.test(t))
        return true;
    if (/^\*\*[^*]+\*\*/.test(t))
        return true;
    return t.length > 40;
}
/** Remove isolated agent meta lines from document deliverable text. */
export function sanitizeWhaleDeliverable(text) {
    let lines = String(text ?? "").split("\n");
    while (lines.length > 0) {
        const first = lines[0].trim();
        if (!first) {
            lines.shift();
            continue;
        }
        if (OPENING_META_RE.test(first) && !isDeliverableContentLine(first)) {
            lines.shift();
            continue;
        }
        break;
    }
    while (lines.length > 0) {
        const last = lines[lines.length - 1].trim();
        if (!last) {
            lines.pop();
            continue;
        }
        if (CLOSING_META_RE.test(last) && !isDeliverableContentLine(last)) {
            lines.pop();
            continue;
        }
        break;
    }
    return lines.join("\n").trim();
}
/** Human-readable body for Wave agent_task / agent_response widgets (hides routing JSON). */
export function formatAgentWidgetDisplayText(raw) {
    let text = String(raw ?? "").trim();
    if (!text)
        return "";
    text = text.replace(/^```(?:json)?\s*\n[\s\S]*?\n```\s*/i, "").trim();
    const parsed = tryParseLeadingJsonObject(text);
    if (parsed) {
        const { obj, rest } = parsed;
        const instruction = String(obj.agent_instruction ?? "").trim();
        if (instruction)
            return cleanupAgentMarkdown(instruction);
        const output = String(obj.output ?? "").trim();
        if (output)
            return cleanupAgentMarkdown(output);
        if (obj.selected_agent != null && obj.status != null) {
            if (rest)
                return formatAgentWidgetDisplayText(rest);
            return "";
        }
        if (rest)
            return formatAgentWidgetDisplayText(rest);
        return "";
    }
    return cleanupAgentMarkdown(text);
}
export function isAgentWidgetRelation(relation) {
    return relation === "agent_task" || relation === "agent_response";
}
/** Chat-safe agent reply (markdown, no orchestrator JSON). */
export function formatWhaleChatReply(text) {
    const formatted = formatAgentWidgetDisplayText(text);
    const base = formatted || cleanupAgentMarkdown(String(text ?? "").trim());
    return sanitizeWhaleDeliverable(base);
}
const ATTACHMENT_MARKER_RE = /\[attachment:([^\]]+)\]/g;
function isImageMissionAttachment(att) {
    if (att.mime?.startsWith("image/"))
        return true;
    if (att.type === "image")
        return true;
    return false;
}
function safeHttpImageUrl(url) {
    const trimmed = String(url ?? "").trim();
    if (!trimmed || !/^https?:\/\//i.test(trimmed))
        return null;
    return trimmed;
}
/** Render-time: replace stable CRDT markers with markdown image syntax (http(s) only). */
export function resolveAttachmentPlaceholders(text, attachments) {
    const source = String(text ?? "");
    if (!source)
        return "";
    if (!attachments.length) {
        return source.replace(ATTACHMENT_MARKER_RE, "").trim();
    }
    const byId = new Map(attachments.map((att) => [att.id, att]));
    const imageAttachments = attachments.filter(isImageMissionAttachment);
    const singleImage = imageAttachments.length === 1 ? imageAttachments[0] : undefined;
    const referenced = new Set();
    const resolved = source
        .replace(ATTACHMENT_MARKER_RE, (match, rawId) => {
        const id = String(rawId ?? "").trim();
        const direct = byId.get(id);
        const directUrl = safeHttpImageUrl(direct?.presignedUrl);
        if (directUrl) {
            referenced.add(id);
            return `![attachment](${directUrl})`;
        }
        if (singleImage) {
            const fallbackUrl = safeHttpImageUrl(singleImage.presignedUrl);
            if (fallbackUrl) {
                referenced.add(singleImage.id);
                return `![attachment](${fallbackUrl})`;
            }
        }
        return "";
    })
        .trim();
    // L'LLM può omettere del tutto il marker pur avendo generato l'immagine:
    // gli attachment image non referenziati vanno comunque mostrati, in coda.
    const unreferenced = imageAttachments
        .filter((att) => !referenced.has(att.id))
        .map((att) => safeHttpImageUrl(att.presignedUrl))
        .filter((url) => url != null)
        .map((url) => `![attachment](${url})`);
    if (unreferenced.length === 0)
        return resolved;
    return [resolved, ...unreferenced].filter(Boolean).join("\n\n");
}
/** Finalize-time: rewrite non-UUID placeholder slugs to stable `[attachment:<uuid>]` markers. */
export function normalizeAttachmentPlaceholders(text, attachments) {
    const source = String(text ?? "");
    if (!source || !attachments.length)
        return source;
    const imageAttachments = attachments.filter(isImageMissionAttachment);
    if (imageAttachments.length !== 1)
        return source;
    const stableId = imageAttachments[0].id;
    return source.replace(ATTACHMENT_MARKER_RE, (match, rawId) => {
        const id = String(rawId ?? "").trim();
        if (byIdHas(attachments, id))
            return match;
        return `[attachment:${stableId}]`;
    });
}
function byIdHas(attachments, id) {
    return attachments.some((att) => att.id === id);
}
